/**
 * The shared preview thumbnail (collaboration phase 4, the docked "In this Project" panel's slot): a small live view of
 * the preview someone else is looking at, at their route, with their pointer on it, and when it last reloaded after a
 * save: "Shared preview · Sara's pointer · reloaded 8 s ago after Luca's save". A click opens YOUR preview there;
 * Enlarge shows the same live view big (their route, their pointer, the reloads), with Open in my preview and Follow.
 *
 * It is the same preview URL in a scaled, inert iframe, loaded with your own session: the preview's access rules apply
 * as when you open it (nothing of theirs is streamed, only their route and pointer through presence). The URL is the
 * one the runner gives for their service (`previewUrl` in the project's services list, the same list for an owner, an
 * editor and a viewer), else Flow's path-based `/api/preview/<project>/services/<service>/proxy/`.
 *
 * Only a preview that is up is loaded: one that is not running (or still starting) shows that in words, "Guglielmo’s
 * preview is not running", instead of an iframe whose page load would say "Starting your preview" for ever (or start
 * a build in the owner's sandbox). It loads by itself once the service is up. Open in my preview is offered while it
 * runs, or to someone who may start it (canRun: an editor or the owner, not a viewer).
 *
 * Props: `snapshot` (useProjectPresence), `projectId`; `personId` to show one person (default: the other person who
 * moved on a preview last); `baseUrlFor(service)` the preview's base URL (default: the runner's, as above); `width` in
 * px (the iframe renders at 1280×800, scaled down).
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal } from 'antd';
import { ArrowsOut } from '@phosphor-icons/react';
import config from '../../config';
import useProjectServices from '../../hooks/useProjectServices';
import { canonicalServiceName, isRunning } from '../../services/SandboxService';
import { isPreviewReady, serviceEntryFor } from '../../utils/previewReadiness';
import { followPerson } from '../../utils/presenceClient';
import { firstName, othersOf } from '../../utils/presenceModel';
import { joinRoute, previewOfPerson, serviceNameOf } from '../../utils/collab3Model';
import { inkFor } from '../../utils/participantColors';
import { openPreviewFor } from '../presence/Presence';
import './Collab.css';

const FRAME_W = 1280;
const FRAME_H = 800;
const POINTER_FRESH_MS = 10000;

/**
 * Flow's path-based preview of a service, on Flow's public origin: `/api/preview/<project>/services/<service>/proxy/`
 * (`web` and `services/web` alike; the runner's public path keeps the `services/` folder). Null for the project root.
 */
export function defaultPreviewBase(projectId, service) {
  const folder = service ? canonicalServiceName(service) : '.';
  if (!projectId || folder === '.') { return null; }
  const origin = (config.publicBaseUrl || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/$/, '');
  return `${origin}/api/preview/${encodeURIComponent(projectId)}/${folder.split('/').map(encodeURIComponent).join('/')}/proxy/`;
}

/**
 * Where their service is, from the project's services answer: `ready` (up, with a URL), `starting` (building, or up
 * without its URL yet), `stopped` (not running, or never started), `checking` (no answer yet) or `unknown` (the
 * runner could not be asked).
 */
export function sharedPreviewState({ services, loaded, unavailable }, service) {
  if (!loaded) { return 'checking'; }
  const entry = serviceEntryFor(services, service);
  if (isPreviewReady(entry)) { return 'ready'; }
  if (entry && isRunning(entry) && !entry.crashed && entry.buildStatus !== 'failed' && entry.buildStatus !== 'error') { return 'starting'; }
  return unavailable && !entry ? 'unknown' : 'stopped';
}

/** What the view says when it shows no app: `{ title, hint }`. */
export function notShownText(state, name, canRun) {
  if (state === 'checking') { return { title: `Looking for ${name}’s preview…`, hint: '' }; }
  if (state === 'starting') { return { title: `${name}’s preview is starting…`, hint: 'It shows here once the app is up.' }; }
  if (state === 'unknown') { return { title: `${name}’s preview can’t be reached right now`, hint: 'It shows here once the sandbox answers.' }; }
  return {
    title: `${name}’s preview is not running`,
    hint: canRun ? 'Open in my preview to start it.' : 'It shows here once someone who can edit the project starts it.',
  };
}

/** Whose preview to show: `personId`'s, else the other person who moved on a preview last; with their view. */
export function previewTarget(snapshot, personId = null) {
  const candidates = othersOf(snapshot)
    .filter((person) => !personId || person.id === personId)
    .map((person) => {
      const views = [...(person.views || [])].sort((a, b) => (b.at || 0) - (a.at || 0));
      const view = views.find((v) => v.kind === 'preview');
      return view && previewOfPerson(person) ? { person, view, ...previewOfPerson(person) } : null;
    })
    .filter(Boolean)
    .sort((a, b) => (b.view.at || 0) - (a.view.at || 0));
  return candidates[0] || null;
}

/** "8 s ago", "3 min ago". */
export function agoShort(ms) {
  const seconds = Math.max(1, Math.round(ms / 1000));
  if (seconds < 60) { return `${seconds} s ago`; }
  const minutes = Math.round(seconds / 60);
  return minutes < 60 ? `${minutes} min ago` : `${Math.round(minutes / 60)} h ago`;
}

/** "reloaded 8 s ago after Luca's save", "after Flow chat's save for Luca", or null. */
export function reloadLabel(save, now = Date.now()) {
  if (!save?.at || !save.by) { return null; }
  const who = save.by.kind === 'agent' ? `${save.by.label || 'an AI agent'} for ${firstName(save.by.name)}` : firstName(save.by.name);
  return `reloaded ${agoShort(now - save.at)} after ${who}’s save`;
}

/** The preview at their route, scaled to `width`, with their pointer; `reloadKey` changes reload it. */
function LiveFrame({ src, reloadKey, pointer, person, name, width, title }) {
  const scale = width / FRAME_W;
  return (
    <>
      <iframe
        aria-hidden="true"
        key={`${src}|${reloadKey}`}
        sandbox="allow-scripts allow-same-origin allow-forms"
        src={src}
        style={{ height: FRAME_H, transform: `scale(${scale})`, width: FRAME_W }}
        tabIndex={-1}
        title={title}
      />
      {pointer && (
        <span
          className="collab-shared-pointer"
          data-testid="shared-pointer"
          style={{ '--presence-ink': inkFor(person.slot), left: `${pointer.x * 100}%`, top: `${pointer.y * 100}%` }}
        >
          <span className="collab-shared-pointer-flag">{person.initials || name}</span>
        </span>
      )}
    </>
  );
}

LiveFrame.propTypes = {
  name: PropTypes.string,
  person: PropTypes.object.isRequired,
  pointer: PropTypes.object,
  reloadKey: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  width: PropTypes.number.isRequired,
};

/** The enlarged view's width: as wide as the window allows, at most the preview's own 1280 px. */
const bigWidth = () => Math.max(320, Math.min(FRAME_W, (typeof window !== 'undefined' ? window.innerWidth : FRAME_W) - 96));

export default function SharedPreviewThumbnail({ snapshot, projectId, personId = null, baseUrlFor = null, width = 240 }) {
  const [now, setNow] = useState(() => Date.now());
  const [enlarged, setEnlarged] = useState(false);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const target = previewTarget(snapshot, personId);
  const run = useProjectServices(projectId, { enabled: Boolean(projectId && target) });
  const { services, canRun } = run;

  if (!projectId || !target) { return null; }
  const service = serviceNameOf(target.service);
  const entry = serviceEntryFor(services, target.service);
  const base = baseUrlFor ? baseUrlFor(service) : (entry?.previewUrl || defaultPreviewBase(projectId, target.service));
  const src = base ? joinRoute(base, target.route) : null;
  if (!src) { return null; }
  const state = sharedPreviewState(run, target.service);
  const shown = state === 'ready';
  const mayOpen = shown || Boolean(canRun);
  const save = snapshot?.previews?.[service] || null;
  const pointer = target.view.pointer && now - (target.view.pointer.at || 0) <= POINTER_FRESH_MS ? target.view.pointer : null;
  const name = firstName(target.person.name);
  const caption = ['Shared preview', pointer ? `${name}’s pointer` : `${name} is here`, reloadLabel(save, now)].filter(Boolean).join(' · ');
  // A new save reloads it, as theirs did. (A service that comes up is loaded then: no frame is shown before.)
  const reloadKey = `${save?.at || 0}`;
  const openMine = () => openPreviewFor(projectId, { service: target.service, route: target.route });
  const notShown = shown ? null : notShownText(state, name, canRun);
  const frame = (w) => (shown
    ? <LiveFrame name={name} person={target.person} pointer={pointer} reloadKey={reloadKey} src={src} title={`${name}’s preview`} width={w} />
    : (
      <span className="collab-shared-preview-empty" data-testid="shared-preview-not-shown" role="status">
        <strong>{notShown.title}</strong>
        {notShown.hint && <span>{notShown.hint}</span>}
      </span>
    ));
  const big = bigWidth();

  return (
    <figure className="collab-shared-preview" data-testid="shared-preview">
      <div className="collab-shared-preview-wrap">
        <button
          aria-label={shown ? `See ${name}’s view: ${service} at ${target.route}` : notShown.title}
          className="collab-shared-preview-frame"
          disabled={!mayOpen}
          onClick={mayOpen ? openMine : undefined}
          style={{ height: Math.round(FRAME_H * (width / FRAME_W)), width }}
          type="button"
        >
          {frame(width)}
        </button>
        {shown && <span aria-hidden="true" className="collab-live-badge"><span className="collab-live-dot" />Live</span>}
        <button aria-label={`Enlarge ${name}’s view`} className="collab-shared-enlarge" onClick={() => setEnlarged(true)} title="Enlarge" type="button">
          <ArrowsOut aria-hidden="true" size={14} />
        </button>
      </div>
      <figcaption className="collab-muted collab-small">{caption} · <span className="collab-shared-route">{target.route}</span></figcaption>
      <Modal
        centered
        destroyOnHidden
        footer={(
          <>
            <Button onClick={() => followPerson(projectId, target.person.id)}>Follow {name}</Button>
            {mayOpen && <Button onClick={() => { setEnlarged(false); openMine(); }} type="primary">Open in my preview</Button>}
          </>
        )}
        onCancel={() => setEnlarged(false)}
        open={enlarged}
        title={`${name}’s view, live · ${service} ${target.route}`}
        width={big + 48}
      >
        <div className="collab-shared-preview-frame is-big" data-testid="shared-preview-big" style={{ height: Math.round(FRAME_H * (big / FRAME_W)), width: big }}>
          {frame(big)}
        </div>
        <p className="collab-muted collab-small">{shown ? `${caption}. It follows their route and pointer and reloads after each save; you can’t click in it.` : caption}</p>
      </Modal>
    </figure>
  );
}

SharedPreviewThumbnail.propTypes = {
  baseUrlFor: PropTypes.func,
  personId: PropTypes.string,
  projectId: PropTypes.string,
  snapshot: PropTypes.object,
  width: PropTypes.number,
};
